import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import { FaArrowLeft } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store/store';
import { addPayoutMethodV1 } from '../../store/reducers/payoutReducer';
import type { StripeCardNumberElementChangeEvent } from '@stripe/stripe-js';
import './AddPayoutCards.css';
import { useAppSelector } from '../../store/hooks'; // adjust if needed
        
// const stripePromise = loadStripe(
//   'pk_test_51PX66aCTXHQWQf5MIvqOA1jYIwQwCQnKilmGggQi82B31irmsVLzaPzEFmvXp5sKVFKQjC9mAXzCQ9Gx4mZmmMV6000rbBQav6'
// );
const stripePromise = loadStripe(
  'pk_live_51PX66aCTXHQWQf5M7rEymc9OKIMthsotT3xaMCF91VzjnSJjhAR4T7krET5YDipGutfCLXu3i6ptSaXKZVydnBcn00TIDF42nT'
);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      iconColor: '#555',
      color:     '#333',
      fontSize:  '16px',
      '::placeholder': { color: '#999' },
    },
    invalid: {
      color: '#fa755a',
    },
  },
};

interface AddCardFormProps {
  defaultAccountId?: string;
}

const AddCardForm: React.FC<AddCardFormProps> = ({ defaultAccountId }) => {
  const stripe    = useStripe();
  const elements  = useElements();
  const navigate  = useNavigate();
  const dispatch  = useDispatch<AppDispatch>();

  const { isAddingV1, addV1Error } = useSelector((state: RootState) => state.payout);

  const [fullName, setFullName]       = useState('');
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [cardBrand, setCardBrand]     = useState<string>('');
  const [submitted, setSubmitted]     = useState(false);



  useEffect(() => {
    if (submitted && !isAddingV1 && !addV1Error) {
      navigate(-1);
    }
  }, [submitted, isAddingV1, addV1Error, navigate]);

  const handleCardNumberChange = (e: StripeCardNumberElementChangeEvent) => {
    setCardBrand(e.brand);
    setStripeError(e.error?.message ?? null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStripeError(null);

    if (!stripe || !elements) {
      setStripeError('Stripe.js not loaded yet.');
      return;
    }

    const cardEl = elements.getElement(CardNumberElement);
    if (!cardEl) {
      setStripeError('Card number field not found.');
      return;
    }

    const { token, error: tokenErr } = await stripe.createToken(cardEl, {
      name: defaultAccountId ? fullName.trim() : fullName.trim(), 
      currency: 'usd',
    });
    if (tokenErr || !token) {
      setStripeError(tokenErr?.message ?? 'Failed to create token');
      return;
    }

    dispatch(
      addPayoutMethodV1({
        accountId:      defaultAccountId ?? '' ,
        tokenId:        token.id
      })
    );
    setSubmitted(true);
  };

  const errorMessage = stripeError || addV1Error;

  return (
    <div className="add-card-container">
      <div className="add-card-header">
        <FaArrowLeft
          className="back-button"
          onClick={() => navigate(-1)}
        />
        <h1>Add New Card</h1>
      </div>

      <form className="card-form" onSubmit={handleSubmit}>


        <label>
          Full Name
          <input
            type="text"
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            placeholder="Cardholder Name"
            required
          />
        </label>

        <label>
          Card Number
          <div className="stripe-input">
            <CardNumberElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={handleCardNumberChange}
            />
            {cardBrand && (
              <span className="card-brand">{cardBrand.toUpperCase()}</span>
            )}
          </div>
        </label>

        <div className="small-inputs">
          <label>
            Expiry Date
            <div className="stripe-input">
              <CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
            </div>
          </label>
          <label>
            CVC
            <div className="stripe-input">
              <CardCvcElement options={CARD_ELEMENT_OPTIONS} />
            </div>
          </label>
        </div>

        {errorMessage && (
          <div className="error-text">{errorMessage}</div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={!stripe || isAddingV1}
          >
            {isAddingV1 ? 'Processing…' : 'Add Card'}
          </button>
        </div>
      </form>
    </div>
  );
};

export const AddPayoutCards: React.FC = () => {
  const accountId = useAppSelector(
    (state: RootState) => state.auth.profile?.stripeConnAccId
  );



  return (
    <Elements stripe={stripePromise}>
      <AddCardForm defaultAccountId={accountId} />
    </Elements>
  );
};

export default AddPayoutCards;